import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronLeft } from 'lucide-react';
import type { TicketDto, TicketStatus } from '@bstm/shared';
import { api, ApiError } from '../api/client';
import { toast } from 'sonner';
import { useAuth } from '../auth/AuthContext';
import TicketFeed from '../components/TicketFeed';
import VoteWidget from '../components/VoteWidget';

interface AuditEvent {
  id: string;
  actorUserId: string;
  action: string;
  metadata: Record<string, unknown> | null;
  createdAt: number;
}

function statusClass(s: TicketStatus): string {
  return {
    DRAFT: 'bg-zinc-700 text-zinc-300',
    NEEDS_REVIEW: 'bg-amber-600/30 text-amber-300',
    READY_FOR_SUBMISSION: 'bg-indigo-600/30 text-indigo-300',
    SUBMITTED: 'bg-blue-600/30 text-blue-300',
    FILLED: 'bg-emerald-600/30 text-emerald-300',
    CANCELLED: 'bg-red-600/30 text-red-300',
  }[s];
}

function allowedTransitions(
  current: TicketStatus,
  isCreator: boolean,
  isAdmin: boolean,
): TicketStatus[] {
  const canCancel = isCreator || isAdmin;
  switch (current) {
    case 'NEEDS_REVIEW':
      return canCancel ? ['CANCELLED'] : [];
    case 'READY_FOR_SUBMISSION':
      return canCancel ? ['SUBMITTED', 'CANCELLED'] : ['SUBMITTED'];
    case 'DRAFT':
      return ['SUBMITTED', 'CANCELLED'];
    case 'SUBMITTED':
      return ['FILLED', 'CANCELLED'];
    case 'FILLED':
    case 'CANCELLED':
      return [];
  }
}

function transitionLabel(s: TicketStatus): string {
  if (s === 'FILLED') return 'Mark FILLED';
  if (s === 'CANCELLED') return 'Cancel';
  if (s === 'SUBMITTED') return 'Submit ticket';
  return `Move to ${s}`;
}

function transitionButtonClass(s: TicketStatus): string {
  if (s === 'FILLED' || s === 'SUBMITTED') return 'btn-primary';
  if (s === 'CANCELLED') return 'btn-danger';
  return 'btn-secondary';
}

export default function TicketDetail() {
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const { user } = useAuth();

  const q = useQuery<{ ticket: TicketDto }>({
    queryKey: ['ticket', id],
    queryFn: () => api(`/tickets/${id}`),
    enabled: Boolean(id),
  });

  const auditQ = useQuery<{ events: AuditEvent[] }>({
    queryKey: ['ticket', id, 'audit'],
    queryFn: () => api(`/tickets/${id}/audit`),
    enabled: Boolean(id),
  });

  const patch = useMutation({
    mutationFn: (body: { status: TicketStatus }) =>
      api<{ ticket: TicketDto }>(`/tickets/${id}`, { method: 'PATCH', json: body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ticket', id] });
      qc.invalidateQueries({ queryKey: ['ticket', id, 'audit'] });
      qc.invalidateQueries({ queryKey: ['tickets'] });
      toast.success('Updated');
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Failed'),
  });

  if (q.isLoading) return <div className="p-6 text-zinc-500">Loading…</div>;
  const t = q.data?.ticket;
  if (!t) return <div className="p-6 text-zinc-500">Not found</div>;

  const creatorLabel = t.creatorName ?? t.creatorPhone ?? t.userId.slice(0, 8);
  const isCreator = user?.id === t.userId;
  const isAdmin = user?.role === 'ADMIN';
  const transitions = allowedTransitions(t.status, isCreator, isAdmin);

  return (
    <div className="p-4 md:p-6 max-w-3xl">
      <Link to="/" className="inline-flex items-center text-sm text-zinc-400 hover:text-white mb-4">
        <ChevronLeft size={16} /> Tickets
      </Link>

      <div className="card p-4 md:p-6 space-y-4">
        <div className="flex items-baseline justify-between gap-3">
          <div className="min-w-0">
            <div className="font-mono text-xl md:text-2xl truncate">{t.ticker}</div>
            <div className={`font-medium ${t.side === 'BUY' ? 'text-emerald-400' : 'text-red-400'}`}>
              {t.side} {t.shares.toLocaleString()} shares
            </div>
          </div>
          <span className={`px-2 py-1 rounded text-xs whitespace-nowrap ${statusClass(t.status)}`}>
            {t.status.replace(/_/g, ' ')}
          </span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 text-sm text-zinc-400 pt-2 border-t border-zinc-800">
          <div>
            <div className="text-zinc-500 text-xs">Submitted by</div>
            <div className="font-mono text-zinc-200">{creatorLabel}</div>
          </div>
          <div>
            <div className="text-zinc-500 text-xs">Created</div>
            <div className="text-zinc-200">{new Date(t.createdAt * 1000).toLocaleString()}</div>
          </div>
          {t.filledAt && (
            <div>
              <div className="text-zinc-500 text-xs">Filled</div>
              <div className="text-emerald-400">{new Date(t.filledAt * 1000).toLocaleString()}</div>
            </div>
          )}
        </div>

        <div>
          <div className="text-sm text-zinc-400 mb-1">Reason</div>
          <div className="whitespace-pre-wrap text-zinc-200">{t.reason}</div>
        </div>

        {t.sellCircumstances && (
          <div>
            <div className="text-sm text-zinc-400 mb-1">Sell circumstances</div>
            <div className="whitespace-pre-wrap text-zinc-200">{t.sellCircumstances}</div>
          </div>
        )}

        {t.sellChecklist && t.sellChecklist.length > 0 && (
          <div>
            <div className="text-sm text-zinc-400 mb-1">Sell checklist</div>
            <ul className="space-y-1">
              {t.sellChecklist.map((q, i) => (
                <li key={i} className="flex items-start gap-2 text-zinc-200">
                  <span className="text-zinc-500 font-mono">{i + 1}.</span>
                  <span>{q}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {t.buyMoreCircumstances && (
          <div>
            <div className="text-sm text-zinc-400 mb-1">Buy-more circumstances</div>
            <div className="whitespace-pre-wrap text-zinc-200">{t.buyMoreCircumstances}</div>
          </div>
        )}

        {t.buyMoreChecklist && t.buyMoreChecklist.length > 0 && (
          <div>
            <div className="text-sm text-zinc-400 mb-1">Buy-more checklist</div>
            <ul className="space-y-1">
              {t.buyMoreChecklist.map((q, i) => (
                <li key={i} className="flex items-start gap-2 text-zinc-200">
                  <span className="text-zinc-500 font-mono">{i + 1}.</span>
                  <span>{q}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <VoteWidget ticket={t} />

        <TicketFeed ticketId={t.id} />

        {transitions.length > 0 && (
          <div className="pt-4 border-t border-zinc-800">
            <div className="text-sm text-zinc-400 mb-2">Change status</div>
            <div className="flex flex-wrap gap-2">
              {transitions.map((s) => (
                <button
                  key={s}
                  onClick={() => patch.mutate({ status: s })}
                  disabled={patch.isPending}
                  className={transitionButtonClass(s)}
                >
                  {transitionLabel(s)}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="card mt-4 overflow-hidden">
        <div className="px-4 py-2 bg-zinc-950 text-sm text-zinc-400">History</div>
        <ul className="divide-y divide-zinc-800">
          {auditQ.data?.events.map((e) => (
            <li key={e.id} className="px-4 py-2 text-sm flex items-baseline gap-3">
              <span className="text-zinc-500 text-xs font-mono w-44 shrink-0">
                {new Date(e.createdAt * 1000).toLocaleString()}
              </span>
              <span className="text-zinc-200">{e.action}</span>
              {e.metadata && (
                <span className="text-xs text-zinc-500 font-mono truncate">
                  {Object.entries(e.metadata)
                    .map(([k, v]) => `${k}=${typeof v === 'string' ? v : JSON.stringify(v)}`)
                    .join(', ')}
                </span>
              )}
            </li>
          ))}
          {(auditQ.data?.events.length ?? 0) === 0 && (
            <li className="px-4 py-3 text-sm text-zinc-500">No events.</li>
          )}
        </ul>
      </div>
    </div>
  );
}
